import { AsyncStateAlert } from '@ui/general';
import { createAsyncSelectors } from '@lib/selectors';
import { ErrorOrFailStatusCode } from '@sonar-software/sonar-http';
import { GatewayCard } from '../general';
import { isSubdomain } from '@sonar-software/sonar-ts-lib';
import { OidcProviders } from './OIDCProviders';
import { securityActions } from '@sonar-software/sonar-security';
import { useNavigate } from 'react-router-dom';

import { AUTOCOMPLETE, COPY_SPECIFIC, FULL_ROUTES, NOTIFICATIONS_COPY } from '@sonar-software/sonar-ts-constants';
import { Button, Form, Row } from 'antd';
import { Email, Password } from '@ui/form-items';
import React, { useCallback } from 'react';
import { useAction, useAsyncState } from '@hooks';

interface LoginFormValues {
	email: string;
	password: string;
}

const loginSelectors = createAsyncSelectors((state) => state.security.authentication.login.post);

export const LoginView: React.FC = () => {
	const navigate = useNavigate();
	const { isErrored, isFailed, isReceived, isRequested } = useAsyncState(loginSelectors);

	const login = useAction(securityActions.requestPostSecurityAuthorizationLogin);
	const resetLogin = useAction(securityActions.resetPostSecurityAuthorizationLogin);

	const loading = isReceived || isRequested;
	const isInvalid = isErrored || isFailed;

	const handleOnFinish = useCallback(
		(values: LoginFormValues) => {
			const { email, password } = values;

			login({
				email,
				password
			});
		},
		[ login ]
	);

	const handleOnValuesChange = useCallback(() => {
		if (isInvalid) {
			resetLogin();
		}
	}, [ isInvalid, resetLogin ]);

	const handleOnForgotPasswordClick = useCallback(() => {
		navigate(FULL_ROUTES.FORGOT_PASSWORD);
	}, [ navigate ]);

	return (
		<GatewayCard
			id='login'
			title={ COPY_SPECIFIC.LOGIN }>
			<Form
				onFinish={ handleOnFinish }
				onValuesChange={ handleOnValuesChange }>
				<AsyncStateAlert
					byStatusCode={ [
						{
							options: {
								description: NOTIFICATIONS_COPY.FAILED_LOGIN_LOCKED,
								type: 'error'
							},
							statusCode: ErrorOrFailStatusCode.Locked
						}
					] }
					errored={ NOTIFICATIONS_COPY.ERROR_LOGIN }
					failed={ NOTIFICATIONS_COPY.FAILED_LOGIN }
					stateSelector={ loginSelectors.state } />
				<Email
					autoComplete={ AUTOCOMPLETE.USERNAME }
					autoFocus
					disabled={ loading }
					id='login-email'
					placeholder={ COPY_SPECIFIC.EMAIL }
					size='large'
					skipValidation />
				<Password
					autoComplete={ AUTOCOMPLETE.CURRENT_PASSWORD }
					disabled={ loading }
					id='login-password'
					size='large'
					skipValidation />
				<Row justify='space-between'>
					<Button
						disabled={ loading }
						id='forgot-password-button'
						onClick={ handleOnForgotPasswordClick }
						size='large'>
						{ COPY_SPECIFIC.FORGOT_PASSWORD }
					</Button>
					<Button
						htmlType='submit'
						id='login-submit-button'
						loading={ loading }
						size='large'
						type='primary'>
						{ COPY_SPECIFIC.LOGIN }
					</Button>
				</Row>
			</Form>
			{ isSubdomain(window.location.href) && <OidcProviders /> }
		</GatewayCard>
	);
};
